import { ArrowLeft, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useActionsFed, useAgents, useBonCommande, useBudgetLignes, useFacture, useFed, useParametres, usePiecesJointes, useStructures, type FedSaisie } from '@/api/hooks';
import { LIBELLE_NATURE, type Fed, type NatureFed } from '@/api/types';
import { voitToutLeFonds } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { BoutonPdf, Chargement, PageHeader } from '@/components/common';
import { ActiviteSousActivite, CircuitPrevu, Completude, Lateral, montantEnLettres, SituationLigneChoisie, useDeclareMemorial, type PointCompletude } from '@/components/documents';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { plusJoursOuvres } from '@/components/tresorerie';
import { cn, dateFr, montant } from '@/lib/utils';

/** Contre-valeurs par défaut en USD (modifiables dans le formulaire ; les mêmes que l'API). */
const TAUX_DEFAUT: Record<string, number> = { USD: 1, CDF: 1 / 2850, EUR: 1.08 };
/** Natures de la maquette (§ 6) avec leur explication ; les natures de trésorerie restent disponibles. */
const NATURES: Array<{ cle: NatureFed; sous: string }> = [
  { cle: 'PERDIEMS', sous: 'perdiems, frais, avances' },
  { cle: 'FOURNISSEUR', sous: 'paiement extérieur : facture et commande ou contrat' },
  { cle: 'AUTRE', sous: 'note justificative' },
  { cle: 'RETOUR_CAISSE', sous: 'trésorerie' },
  { cle: 'NIVELLEMENT', sous: 'trésorerie' },
];

interface LigneValeurs {
  description: string;
  beneficiaireType: 'AGENT' | 'FOURNISSEUR';
  agentId: string;
  beneficiaireLibelle: string;
  montant: number;
}

interface Valeurs {
  objet: string;
  ligneBudgetaireId: string;
  nature: NatureFed;
  devise: 'USD' | 'CDF' | 'EUR';
  tauxChange: number;
  modePaiement: string;
  semaineDecaissement: string;
  urgence: boolean;
  memorial: boolean;
  lignes: LigneValeurs[];
}

const ligneVide = (): LigneValeurs => ({ description: '', beneficiaireType: 'FOURNISSEUR', agentId: '', beneficiaireLibelle: '', montant: 0 });

const depuisFed = (f: Fed): Valeurs => ({
  objet: f.objet,
  ligneBudgetaireId: f.ligneBudgetaireId,
  nature: f.nature,
  devise: (f.devise as Valeurs['devise']) ?? 'USD',
  tauxChange: f.montant > 0 ? Math.round((f.montantUsd / f.montant) * 1_000_000) / 1_000_000 : TAUX_DEFAUT[f.devise] ?? 1,
  modePaiement: f.modePaiement ?? '',
  semaineDecaissement: f.semaineDecaissement ?? '',
  urgence: f.urgence,
  memorial: f.memorial,
  lignes: f.lignes.map((l) => ({ description: l.description, beneficiaireType: l.beneficiaireType, agentId: l.agentId ?? '', beneficiaireLibelle: l.beneficiaireLibelle ?? '', montant: l.montant })),
});

const versSaisie = (v: Valeurs): FedSaisie => ({
  objet: v.objet.trim(),
  ligneBudgetaireId: v.ligneBudgetaireId,
  nature: v.nature,
  devise: v.devise,
  tauxChange: v.devise === 'USD' ? undefined : Number(v.tauxChange) || TAUX_DEFAUT[v.devise],
  modePaiement: v.modePaiement || undefined,
  semaineDecaissement: v.semaineDecaissement || undefined,
  urgence: v.urgence,
  memorial: v.memorial,
  lignes: v.lignes
    .filter((l) => l.description.trim())
    .map((l) => ({
      description: l.description.trim(),
      beneficiaireType: l.beneficiaireType,
      agentId: l.beneficiaireType === 'AGENT' ? l.agentId : undefined,
      beneficiaireLibelle: l.beneficiaireType === 'FOURNISSEUR' ? l.beneficiaireLibelle.trim() : undefined,
      montant: Number(l.montant) || 0,
    })),
});

/** FED hors TDR (maquette émetteur, § 6) : rattachement, nature, lignes, paiement et calendrier, pièces, aperçu et soumission. */
export function FedFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const parametres = useParametres();
  const delaiMinimal = Number(parametres.data?.find((x) => x.cle === 'tresorerie.delaiMinDecaissementJours')?.valeur ?? 3);
  const dateMinimale = plusJoursOuvres(new Date().toISOString().slice(0, 10), delaiMinimal);
  const u = session!.utilisateur;
  const existant = useFed(id);
  const agents = useAgents();
  const structures = useStructures();
  const declareMemorial = useDeclareMemorial(u.structureCode);
  const pieces = usePiecesJointes('FED', id);
  // Facture d'un bon de commande autorisé : « /fed/nouveau?bc=<id> » préremplit le formulaire.
  const [params] = useSearchParams();
  const bcId = id ? undefined : (params.get('bc') ?? undefined);
  const bc = useBonCommande(bcId);
  // Facture reçue transmise à la CPM : « /fed/nouveau?facture=<id> » préremplit le formulaire (règle R10).
  const factureId = id ? undefined : (params.get('facture') ?? undefined);
  const facture = useFacture(factureId);
  const [toutesLignes, setToutesLignes] = useState(Boolean(bcId || factureId));
  const budget = useBudgetLignes(toutesLignes ? undefined : u.structureCode);
  const actions = useActionsFed();

  const form = useForm<Valeurs>({
    defaultValues: { objet: '', ligneBudgetaireId: '', nature: 'FOURNISSEUR', devise: 'USD', tauxChange: 1, modePaiement: 'Virement', semaineDecaissement: '', urgence: false, memorial: false, lignes: [ligneVide()] },
  });
  const lignes = useFieldArray({ control: form.control, name: 'lignes' });
  const valeurs = useWatch({ control: form.control });

  useEffect(() => {
    if (existant.data) form.reset(depuisFed(existant.data.fed));
  }, [existant.data, form]);

  useEffect(() => {
    if (!bc.data || id) return;
    const b = bc.data.bonCommande;
    form.reset({
      objet: `Facture ${b.fournisseur} · ${b.objet}`.slice(0, 200),
      ligneBudgetaireId: b.ligneBudgetaireId,
      nature: 'FOURNISSEUR',
      devise: 'USD',
      tauxChange: 1,
      modePaiement: 'Virement',
      semaineDecaissement: '',
      urgence: false,
      memorial: false,
      lignes: [{ description: `Facture du bon de commande ${b.numero ?? ''}`.trim(), beneficiaireType: 'FOURNISSEUR', agentId: '', beneficiaireLibelle: b.fournisseur, montant: b.montantUsd }],
    });
  }, [bc.data, id, form]);

  useEffect(() => {
    if (!facture.data || id) return;
    const f = facture.data.facture;
    form.reset({
      objet: `Facture ${f.fournisseur} · ${f.numeroFacture}`.slice(0, 200),
      ligneBudgetaireId: f.ligneBudgetaireId ?? f.bonCommande?.ligneBudgetaireId ?? '',
      nature: 'FOURNISSEUR',
      devise: 'USD',
      tauxChange: 1,
      modePaiement: 'Virement',
      semaineDecaissement: '',
      urgence: false,
      memorial: false,
      lignes: [{ description: `${f.objet} (facture ${f.numeroFacture}${f.bonCommande?.numero ? `, ${f.bonCommande.numero}` : ''})`.slice(0, 200), beneficiaireType: 'FOURNISSEUR', agentId: '', beneficiaireLibelle: f.fournisseur, montant: f.montantUsd }],
    });
  }, [facture.data, id, form]);

  // La nature détermine le type de bénéficiaire par défaut des lignes.
  const nature = valeurs.nature ?? 'FOURNISSEUR';
  useEffect(() => {
    const v = form.getValues();
    v.lignes.forEach((l, i) => {
      if (nature === 'PERDIEMS' && l.beneficiaireType !== 'AGENT' && !l.beneficiaireLibelle) form.setValue(`lignes.${i}.beneficiaireType`, 'AGENT');
      if (nature === 'FOURNISSEUR' && l.beneficiaireType !== 'FOURNISSEUR' && !l.agentId) form.setValue(`lignes.${i}.beneficiaireType`, 'FOURNISSEUR');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nature]);

  const devise = valeurs.devise ?? 'USD';
  const taux = devise === 'USD' ? 1 : Number(valeurs.tauxChange) || TAUX_DEFAUT[devise] || 1;
  const total = (valeurs.lignes ?? []).reduce((s, l) => s + (Number(l?.montant) || 0), 0);
  const totalUsd = Math.round(total * taux * 100) / 100;
  const ligne = (budget.data ?? []).find((l) => l.id === valeurs.ligneBudgetaireId);
  const structure = structures.data?.find((s) => s.code === u.structureCode);
  const paiementExterieur = nature === 'FOURNISSEUR';
  const typesPieces = useMemo(() => new Set((pieces.data?.pieces ?? []).map((p) => p.type)), [pieces.data]);

  // Contrôle avant soumission (maquette : pièces exigées selon la nature, semaine de décaissement, montant).
  const points = useMemo<PointCompletude[]>(() => {
    const v = valeurs;
    const p: PointCompletude[] = [];
    p.push({ ok: Boolean(v.ligneBudgetaireId), message: 'Ligne budgétaire choisie' });
    p.push({ ok: (v.objet?.trim().length ?? 0) >= 5, message: 'Objet renseigné (5 caractères au moins)' });
    const ls = (v.lignes ?? []).filter((l) => l?.description?.trim());
    p.push({ ok: ls.length > 0 && ls.every((l) => (Number(l?.montant) || 0) > 0), message: 'Au moins une ligne avec description et montant' });
    p.push({ ok: ls.every((l) => (l?.beneficiaireType === 'AGENT' ? Boolean(l.agentId) : Boolean(l?.beneficiaireLibelle?.trim()))), message: 'Bénéficiaire de chaque ligne' });
    if (devise !== 'USD') p.push({ ok: taux > 0, message: 'Contre-valeur en USD' });
    p.push({ ok: Boolean(v.semaineDecaissement && v.semaineDecaissement >= dateMinimale), message: `Semaine de décaissement au plus tôt ${delaiMinimal} jour(s) ouvré(s) après la soumission` });
    if (paiementExterieur) {
      p.push({ ok: typesPieces.has('FACTURE') || typesPieces.has('PROFORMA'), message: 'Pièce : facture (ou facture proforma)' });
      p.push({ ok: typesPieces.has('BON_COMMANDE') || typesPieces.has('CONTRAT'), message: 'Pièce : bon de commande ou contrat' });
    }
    if (ligne) p.push({ ok: totalUsd <= ligne.disponiblePrevisionnel + 0.005, message: `Montant dans le disponible de la ligne (${montant(ligne.disponiblePrevisionnel)})`, avertissement: true });
    return p;
  }, [valeurs, devise, taux, dateMinimale, delaiMinimal, paiementExterieur, typesPieces, ligne, totalUsd]);
  const bloquants = points.filter((p) => !p.ok && !p.avertissement);

  const enregistrer = async (puisSoumettre: boolean) => {
    const valide = await form.trigger();
    const v = form.getValues();
    const corps: FedSaisie = { ...versSaisie(v), ...(bcId ? { bonCommandeId: bcId } : {}), ...(factureId ? { factureId } : {}) };
    if (!valide || !corps.lignes.length) {
      toast.error("Renseignez l'objet, la ligne budgétaire et au moins une ligne avec description et montant.");
      return;
    }
    try {
      const r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
      if (!puisSoumettre) {
        toast.success(id ? 'FED enregistré.' : 'FED enregistré en brouillon.');
        navigate(`/fed/${r.fed.id}`);
        return;
      }
      try {
        await actions.soumettre.mutateAsync(r.fed.id);
        toast.success('FED soumis au circuit de validation.');
      } catch (e) {
        const controles = e instanceof ApiError ? (e.corps as { controles?: Array<{ message: string }> } | undefined)?.controles : undefined;
        toast.error(controles?.length ? `Soumission refusée : ${controles.map((c) => c.message).join(' ; ')}` : e instanceof Error ? e.message : 'Soumission impossible.');
      }
      navigate(`/fed/${r.fed.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  if (id && existant.isLoading) return <Chargement />;
  const enCours = actions.creer.isPending || actions.modifier.isPending || actions.soumettre.isPending;
  const champObligatoire = <span className="text-red-600"> *</span>;

  return (
    <form onSubmit={(e) => (e.preventDefault(), enregistrer(false))} className="space-y-5">
      <PageHeader
        retour={
          <Link to={id ? `/fed/${id}` : '/fed'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Corriger le FED ${existant.data?.fed.numero ?? ''}` : 'Nouveau FED hors TDR'}
        sousTitre={`Émis pour ${u.structureCode} par ${u.prenom} ${u.nom}. Pour les dépenses courantes sans termes de référence ; les FED liés à un TDR sont générés depuis le TDR autorisé ou émis par les Achats.`}
        actions={
          <Button type="submit" variant="outline" disabled={enCours}>
            {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
          </Button>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>1. Rattachement budgétaire</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="objet">Objet{champObligatoire}</Label>
                <Input id="objet" {...form.register('objet', { required: true, minLength: 5 })} placeholder="Facture Voyages Horizon SARL · billets Kinshasa–Bunia" />
              </div>
              <div className="space-y-1.5">
                <ActiviteSousActivite lignes={budget.data ?? []} ligneId={valeurs.ligneBudgetaireId ?? ''} setLigneId={(id) => form.setValue('ligneBudgetaireId', id, { shouldDirty: true, shouldValidate: true })} obligatoire={champObligatoire} />
                {(voitToutLeFonds(u.habilitations) || bcId || factureId) && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox checked={toutesLignes} onCheckedChange={(c) => setToutesLignes(c === true)} />
                    Afficher toutes les lignes du Fonds
                  </label>
                )}
              </div>
              <SituationLigneChoisie ligne={ligne} montantDocument={totalUsd} montantInitial={existant.data && existant.data.fed.statut !== 'BROUILLON' ? existant.data.fed.montantUsd : 0} />
              {declareMemorial && (
                <label className="flex items-center gap-2 text-sm">
                  <Controller control={form.control} name="memorial" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
                  Activité du Mémorial (visa du Coordonnateur avant le Directeur)
                </label>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Nature de la dépense</CardTitle>
              <CardDescription>Elle détermine le type de bénéficiaire et, pour un paiement extérieur, les pièces obligatoires.</CardDescription>
            </CardHeader>
            <CardContent>
              <Controller
                control={form.control}
                name="nature"
                render={({ field }) => (
                  <div className="grid gap-2 sm:grid-cols-3">
                    {NATURES.map((n) => (
                      <button key={n.cle} type="button" onClick={() => field.onChange(n.cle)} className={cn('rounded-lg border px-3 py-2 text-left transition-colors hover:bg-accent', field.value === n.cle && 'border-primary bg-primary/5 ring-1 ring-primary')} aria-pressed={field.value === n.cle}>
                        <div className="text-sm font-medium">{LIBELLE_NATURE[n.cle]}</div>
                        <div className="text-xs text-muted-foreground">{n.sous}</div>
                      </button>
                    ))}
                  </div>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                3. Lignes de la dépense{' '}
                <span className="num float-right">
                  {montant(total, true).replace('USD', devise)}
                  {devise !== 'USD' ? ` · ${montant(totalUsd, true)}` : ''}
                </span>
              </CardTitle>
              <CardDescription>Un bénéficiaire et un montant par ligne ; l'agent est choisi dans l'annuaire, le fournisseur ou prestataire est nommé.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="devise">Devise{champObligatoire}</Label>
                  <SelectNatif id="devise" {...form.register('devise')}>
                    <option value="USD">USD</option>
                    <option value="CDF">CDF</option>
                    <option value="EUR">EUR</option>
                  </SelectNatif>
                </div>
                {devise !== 'USD' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="taux">1 {devise} en USD (taux du jour)</Label>
                    <Input id="taux" type="number" step="0.000001" min={0} {...form.register('tauxChange', { valueAsNumber: true })} />
                  </div>
                )}
              </div>
              {lignes.fields.map((f, i) => {
                const type = valeurs.lignes?.[i]?.beneficiaireType ?? 'FOURNISSEUR';
                return (
                  <div key={f.id} className="grid gap-2 rounded-lg border p-3 md:grid-cols-[2rem_1fr_10rem_1fr_9rem_auto]">
                    <div className="num self-center text-sm text-muted-foreground">{i + 1}</div>
                    <Input placeholder="Description" {...form.register(`lignes.${i}.description`)} />
                    <SelectNatif {...form.register(`lignes.${i}.beneficiaireType`)}>
                      <option value="FOURNISSEUR">Fournisseur / tiers</option>
                      <option value="AGENT">Agent du Fonds</option>
                    </SelectNatif>
                    {type === 'AGENT' ? (
                      <SelectNatif {...form.register(`lignes.${i}.agentId`)}>
                        <option value="">Choisir un agent</option>
                        {(agents.data ?? []).map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.prenom} {a.nom} · {a.structureCode}
                          </option>
                        ))}
                      </SelectNatif>
                    ) : (
                      <Input placeholder="Nom du fournisseur ou prestataire" {...form.register(`lignes.${i}.beneficiaireLibelle`)} />
                    )}
                    <Input className="text-right" type="number" min={0} step="0.01" placeholder={devise} {...form.register(`lignes.${i}.montant`, { valueAsNumber: true })} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => lignes.remove(i)} aria-label="Retirer" disabled={lignes.fields.length === 1}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                );
              })}
              <Button type="button" variant="outline" size="sm" onClick={() => lignes.append(ligneVide())}>
                <Plus className="size-4" /> Ajouter une ligne
              </Button>
              <p className="text-sm">
                <span className="text-muted-foreground">Montant en lettres (généré) : </span>
                <span className="italic">{total > 0 ? montantEnLettres(total, devise) : '—'}</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Paiement et calendrier</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="mode">Mode de paiement proposé{champObligatoire}</Label>
                <SelectNatif id="mode" {...form.register('modePaiement')}>
                  <option value="Virement">Virement bancaire</option>
                  <option value="Chèque">Chèque</option>
                  <option value="Espèces">Espèces</option>
                  <option value="Mobile money">Mobile money</option>
                </SelectNatif>
                <p className="text-xs text-muted-foreground">Proposition : la décision finale revient au responsable de la trésorerie.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Urgence{champObligatoire}</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={!valeurs.urgence ? 'default' : 'outline'} onClick={() => form.setValue('urgence', false)}>
                    Normale
                  </Button>
                  <Button type="button" size="sm" variant={valeurs.urgence ? 'default' : 'outline'} onClick={() => form.setValue('urgence', true)}>
                    Urgente
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="semaine">Semaine de décaissement{champObligatoire}</Label>
                <Input id="semaine" type="date" min={dateMinimale} {...form.register('semaineDecaissement')} />
                <p className="text-xs text-muted-foreground">
                  Au plus tôt {delaiMinimal} jour(s) ouvré(s) après la soumission, soit le {dateFr(dateMinimale)} ; elle fixe la semaine du plan de décaissement de la trésorerie.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Pièces justificatives</CardTitle>
              <CardDescription>{paiementExterieur ? 'Paiement extérieur : la facture (ou proforma) et le bon de commande (ou le contrat) sont obligatoires ; la soumission est bloquée tant qu’ils manquent.' : 'Aucune pièce obligatoire : joignez les justificatifs utiles selon la nature de la dépense.'}</CardDescription>
            </CardHeader>
            <CardContent>
              {id ? (
                <PiecesJointes objetType="FED" objetId={id} typesSuggeres={paiementExterieur ? ['FACTURE', 'PROFORMA', 'BON_COMMANDE', 'CONTRAT', 'AUTRE'] : ['JUSTIFICATIF', 'AUTRE']} titre="Pièces jointes" description={paiementExterieur ? 'Facture ou proforma, puis bon de commande ou contrat.' : undefined} />
              ) : (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Enregistrez le brouillon pour joindre les pièces{paiementExterieur ? ' obligatoires (facture, bon de commande ou contrat)' : ''}.</div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to={id ? `/fed/${id}` : '/fed'}>Abandonner</Link>
            </Button>
            {id && <BoutonPdf chemin={`/pdf/fed/${id}`} nom={`${existant.data?.fed.numero ?? 'FED'}.pdf`} libelle="Aperçu du FED" />}
            <Button type="submit" variant="outline" disabled={enCours}>
              {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
            </Button>
            {(!id || existant.data?.fed.statut === 'BROUILLON') && (
              <Button type="button" disabled={enCours || bloquants.length > 0} title={bloquants.length ? `${bloquants.length} point(s) à compléter` : undefined} onClick={() => enregistrer(true)}>
                <Send className="size-4" /> Soumettre le FED
              </Button>
            )}
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Lateral titre="Contrôle avant soumission">
            <Completude points={points} />
          </Lateral>
          <Lateral titre="Montant">
            <div className="num text-xl font-semibold">{montant(totalUsd)}</div>
            {devise !== 'USD' && (
              <div className="num text-xs text-muted-foreground">
                {montant(total, true).replace('USD', devise)} au taux de {taux} USD
              </div>
            )}
          </Lateral>
          <Lateral titre="Circuit">
            <CircuitPrevu type="FED" montant={totalUsd} operationnel={structure?.operationnelle ?? false} structure={u.structureCode} memorial={Boolean(valeurs.memorial)} />
            <p className="mt-1 text-xs text-muted-foreground">Selon les seuils de délégation ; l'urgence est approuvée par le Directeur Général.</p>
          </Lateral>
        </aside>
      </div>
    </form>
  );
}
